import { trackHeidi2ExploreLaterBannerViewed } from '@heidi/agents/data';
import { trackButtonClicked } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID } from '../../../contracts/heidi-2';
import {
  computeShouldShowHeidi2ExploreLaterBanner,
  getHeidi2Ports,
  heidi2ExploreLaterRestartAtom,
  useHeidi2ExploreLaterState,
} from '../../../data/heidi-2';
import { heidi2OnboardingLaunchAtom } from '../heidi-2-onboarding-launch-atom';
import { heidi2ExploreLaterBannerVisibleAtom } from './heidi-2-explore-later-banner-visible-atom';

const messages = defineMessages({
  body: {
    defaultMessage: 'You can now delegate work to Heidi.',
    id: 'C9e5BUIU61',
    description:
      'Top banner for clinicians who skipped the Heidi II tour; tells them Heidi can now take on work',
  },
  openTour: {
    defaultMessage: 'Explore Heidi II',
    id: 'XUKGGmJc53',
    description: 'Button in the Heidi II explore-later banner that reopens the tour',
  },
  dismissLabel: {
    defaultMessage: 'Dismiss Heidi II banner',
    id: '8G+pXRH765',
    description: 'Accessible label for closing the Heidi II explore-later banner',
  },
});

/**
 * In-flow T4 chrome for clinicians who chose "Explore later" (not queued).
 * Waits until the Evidence tour has closed, then stays until they dismiss
 * this strip or finish the welcome tour.
 */
export const Heidi2ExploreLaterBanner = () => (
  <Heidi2ExploreLaterBannerContent key={useAtomValue(heidi2ExploreLaterRestartAtom)} />
);

const Heidi2ExploreLaterBannerContent = () => {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const state = useHeidi2ExploreLaterState();
  const stripReadiness = ports.useStripReadiness();
  const pathname = ports.usePathname();
  const isClinicalRecordingActive = ports.useIsClinicalRecordingActive(false);
  const markModalAsViewed = ports.useMarkModalAsViewed();
  const trackBannerViewed = trackHeidi2ExploreLaterBannerViewed;
  const setLaunch = useSetAtom(heidi2OnboardingLaunchAtom);
  const setExploreLaterBannerVisible = useSetAtom(heidi2ExploreLaterBannerVisibleAtom);
  const [isHiddenLocally, setIsHiddenLocally] = useState(false);
  const viewTrackedRef = useRef(false);

  const isActive =
    !isHiddenLocally &&
    computeShouldShowHeidi2ExploreLaterBanner({
      ...state,
      isClinicalRecordingActive: false,
    });
  // Claim while active even during recording so lower-priority strips (V2 tour)
  // stay suppressed and the clinical workspace does not reflow. Paint only when
  // recording is idle.
  const holdsSlot = ports.useClaimTopStrip('heidi-2-explore-later', isActive);
  const isVisible = holdsSlot && !isClinicalRecordingActive;
  ports.useReportTopStrip(isVisible);
  // Published without the recording hide, so the Scribe 2 banners stay down
  // through a recording rather than mounting and reflowing the workspace.
  useEffect(() => {
    setExploreLaterBannerVisible(isActive);
    return () => setExploreLaterBannerVisible(false);
  }, [isActive, setExploreLaterBannerVisible]);
  // Scribe arbitration: due while waiting out recording or the Evidence tour,
  // which only delays this strip. Latches as shown once it actually paints.
  const isDue = computeShouldShowHeidi2ExploreLaterBanner({
    ...state,
    isClinicalRecordingActive: false,
    isEvidenceTourClosed: true,
  });
  ports.useReportHeidi2Strip(
    'explore-later',
    stripReadiness.userId,
    isVisible
      ? 'showing'
      : !stripReadiness.isReady ||
          (state.isHeidi2OnboardingEnabled && !state.isBillingReady) ||
          pathname === '/scribe' ||
          pathname.startsWith('/scribe/new-user') ||
          isDue
        ? 'pending'
        : 'ineligible'
  );

  useEffect(() => {
    if (!isVisible || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackBannerViewed();
  }, [isVisible, trackBannerViewed]);

  // Reopening does not close the banner: someone who leaves the tour again
  // part-way through still has not seen it.
  const handleOpenTour = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2ExploreLaterBanner,
      action: ButtonAction.Heidi2ExploreLaterBannerOpenTour,
      label: intl.formatMessage(messages.openTour),
    });
    setLaunch({ startAt: 'handoff' });
  }, [intl, setLaunch]);

  const handleDismiss = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2ExploreLaterBanner,
      action: ButtonAction.Heidi2ExploreLaterBannerDismiss,
      label: intl.formatMessage(messages.dismissLabel),
    });
    setIsHiddenLocally(true);
    markModalAsViewed({
      modal_id: HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
      increment_count: false,
      action: ModalViewAction.dismiss,
    });
  }, [intl, markModalAsViewed]);

  if (!isVisible) return null;

  return (
    // theme-agnostic: the Heidi II brand yellow is the same in both themes, so
    // its gradient stays fixed rather than flipping with dark mode; its text
    // uses the on-light tokens for the same reason.
    <section
      className="relative flex h-9 w-full shrink-0 items-center justify-center gap-3 bg-gradient-to-r from-sunlight-200 via-sunlight-100 via-25% to-sunlight-300 to-60% p-1.5 text-text-primary-on-light"
      data-testid="heidi-2-explore-later-banner"
    >
      <div className="flex items-center gap-2">
        <HeidiLogoMark className="size-4 text-text-primary-on-light" />
        <TypographyCaptionBold>
          <FormattedMessage {...messages.body} />
        </TypographyCaptionBold>
      </div>
      <Button
        type="button"
        variant="surface"
        size="xxs"
        onClick={handleOpenTour}
        data-testid="heidi-2-explore-later-banner-open-tour"
      >
        <FormattedMessage {...messages.openTour} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        iconOnly
        aria-label={intl.formatMessage(messages.dismissLabel)}
        className="absolute end-1.5 text-text-primary-on-light"
        onClick={handleDismiss}
        data-testid="heidi-2-explore-later-banner-dismiss"
      >
        <X />
      </Button>
    </section>
  );
};
